// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\CampaignUploadStep.jsx
// Название: Campaign Upload Step - Clean Design
// Версия: 4.0.0
// Дата: 28.12.2025

import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';

const CampaignUploadStep = ({ campaignId, campaign, campaignType, onNext }) => {
  const [mode, setMode] = useState('database');
  const [csvContent, setCsvContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [manualContacts, setManualContacts] = useState([{ firstName: '', lastName: '', email: '', phone: '' }]);
  const [saveToDb, setSaveToDb] = useState(true);
  
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // ============================================
  // СЕКЦИЯ: Загрузка контактов при монтировании
  // ============================================
  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    setLoadingContacts(true);
    try {
      let allContacts = [];
      
      if (campaignType === 'b2b') {
        const response = await api.get('/contacts?contact_type=employee');
        allContacts = response.data || [];
      } else if (campaignType && campaignType.startsWith('brand')) {
        const [customersRes, leadsRes] = await Promise.all([
          api.get('/contacts?contact_type=customer'),
          api.get('/contacts?contact_type=lead')
        ]);
        allContacts = [...(customersRes.data || []), ...(leadsRes.data || [])];
      } else {
        const response = await api.get('/contacts');
        allContacts = response.data || [];
      }
      
      setContacts(allContacts);
      
      // Автовыбор режима: если есть контакты → "Из базы", иначе "Вручную"
      if (!initialLoadDone) {
        setMode(allContacts.length > 0 ? 'database' : 'manual');
        setInitialLoadDone(true);
      }
    } catch (err) {
      console.error('Load contacts error:', err);
    } finally {
      setLoadingContacts(false);
    }
  };

  // ============================================
  // СЕКЦИЯ: Обработчики
  // ============================================
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setCsvContent(event.target.result);
      setError('');
      setResult(null);
    };
    reader.readAsText(file);
  };

  const saveContactsToDb = async (contactsToSave) => {
    if (!saveToDb || contactsToSave.length === 0) return;
    try {
      await api.post('/contacts/bulk', { contacts: contactsToSave });
    } catch (err) {
      console.error('Save to DB error:', err);
    }
  };

  const handleUpload = async () => {
    setLoading(true);
    setError('');
    try {
      let dataToSend;
      let contactsForDb = [];
      
      if (mode === 'csv') {
        if (!csvContent) { setError('Выберите CSV файл'); setLoading(false); return; }
        dataToSend = { csvContent };
        if (saveToDb) {
          const lines = csvContent.split('\n').filter(l => l.trim());
          const headers = lines[0].toLowerCase().split(',');
          const nameIdx = headers.findIndex(h => h.includes('name'));
          const emailIdx = headers.findIndex(h => h.includes('email'));
          const phoneIdx = headers.findIndex(h => h.includes('phone'));
          for (let i = 1; i < lines.length; i++) {
            const cols = lines[i].split(',');
            const fullName = cols[nameIdx] || '';
            const parts = fullName.trim().split(' ');
            contactsForDb.push({
              first_name: parts[0] || '',
              last_name: parts.slice(1).join(' ') || '',
              email: cols[emailIdx] || '',
              phone: cols[phoneIdx] || ''
            });
          }
        }
      } else if (mode === 'manual') {
        const validContacts = manualContacts.filter(c => c.firstName.trim());
        if (validContacts.length === 0) { setError('Добавьте хотя бы одного получателя'); setLoading(false); return; }
        const csvLines = ['name,email,phone'];
        validContacts.forEach(c => {
          csvLines.push(c.firstName + ' ' + (c.lastName || '') + ',' + (c.email || '') + ',' + (c.phone || ''));
          if (saveToDb) {
            contactsForDb.push({ first_name: c.firstName, last_name: c.lastName, email: c.email, phone: c.phone });
          }
        });
        dataToSend = { csvContent: csvLines.join('\n') };
      } else if (mode === 'database') {
        if (selectedContacts.length === 0) { setError('Выберите получателей'); setLoading(false); return; }
        const csvLines = ['name,email,phone'];
        selectedContacts.forEach(c => csvLines.push(c.first_name + ' ' + c.last_name + ',' + (c.email || '') + ',' + (c.phone || '')));
        dataToSend = { csvContent: csvLines.join('\n') };
      }
      
      await saveContactsToDb(contactsForDb);
      const response = await api.post('/campaigns/' + campaignId + '/upload-recipients', dataToSend);
      setResult(response.data);
      
      // Автоматический переход на следующий шаг через 1 сек
      setTimeout(() => {
        onNext();
      }, 1000);
    } catch (err) {
      setError(err.message || err.error || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  const addContact = () => setManualContacts([...manualContacts, { firstName: '', lastName: '', email: '', phone: '' }]);
  const removeContact = (index) => { if (manualContacts.length > 1) setManualContacts(manualContacts.filter((_, i) => i !== index)); };
  const updateContact = (index, field, value) => { const updated = [...manualContacts]; updated[index][field] = value; setManualContacts(updated); };

  const toggleContact = (contact) => {
    const exists = selectedContacts.find(c => c.id === contact.id);
    if (exists) {
      setSelectedContacts(selectedContacts.filter(c => c.id !== contact.id));
    } else {
      setSelectedContacts([...selectedContacts, contact]);
    }
  };

  const selectAll = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts([...contacts]);
    }
  };

  // ============================================
  // СЕКЦИЯ: Расчёты
  // ============================================
  const rewardAmount = parseFloat(campaign?.reward_amount || 0);
  const totalAmount = selectedContacts.length * rewardAmount;
  const contactTypeLabel = campaignType === 'b2b' ? 'сотрудников' : 'клиентов';

  // ============================================
  // СЕКЦИЯ: Стили
  // ============================================
  const styles = {
    container: { maxWidth: 900 },
    header: { marginBottom: 24 },
    title: { fontSize: 20, fontWeight: 600, color: '#111827', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: 8 },
    subtitle: { color: '#6b7280', fontSize: 14, margin: 0 },
    
    tabs: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 24 },
    tab: { 
      padding: '12px 16px', 
      background: '#f9fafb', 
      border: '2px solid #e5e7eb', 
      borderRadius: 10, 
      fontSize: 14, 
      fontWeight: 500, 
      cursor: 'pointer', 
      textAlign: 'center',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6
    },
    tabActive: { background: '#ecfdf5', borderColor: '#10B981', color: '#059669' },
    tabCount: { background: '#e5e7eb', padding: '2px 8px', borderRadius: 10, fontSize: 12 },
    tabCountActive: { background: '#10B981', color: 'white' },
    
    section: { background: '#f9fafb', borderRadius: 12, padding: 20, marginBottom: 20 },
    
    // Database mode
    dbHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
    dbInfo: { fontSize: 14, color: '#374151' },
    dbInfoCount: { fontWeight: 600, color: '#10B981' },
    selectAllBtn: { background: 'none', border: '1px solid #10B981', color: '#10B981', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 },
    
    dbList: { display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' },
    dbItem: { 
      display: 'grid', 
      gridTemplateColumns: '32px 44px 1fr 160px 130px', 
      alignItems: 'center', 
      gap: 12,
      padding: '10px 12px', 
      background: 'white',
      border: '2px solid #e5e7eb',
      borderRadius: 10,
      cursor: 'pointer',
      transition: 'all 0.15s'
    },
    dbItemSelected: { background: '#f0fdf4', borderColor: '#10B981' },
    checkbox: { width: 20, height: 20, accentColor: '#10B981', cursor: 'pointer' },
    dbPhoto: { width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' },
    dbPhotoPlaceholder: { 
      width: 40, 
      height: 40, 
      borderRadius: '50%', 
      background: '#e5e7eb', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      fontSize: 13, 
      fontWeight: 600, 
      color: '#6b7280' 
    },
    dbName: { fontWeight: 500, color: '#111827', fontSize: 14 },
    dbMeta: { color: '#6b7280', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    noPhotoWarn: { color: '#f59e0b', fontSize: 12, marginLeft: 4 },
    badgeCustomer: { marginLeft: 6, fontSize: 10, padding: '2px 6px', background: '#dbeafe', borderRadius: 4 },
    badgeLead: { marginLeft: 6, fontSize: 10, padding: '2px 6px', background: '#fef3c7', borderRadius: 4 },
    
    emptyDb: { padding: 40, textAlign: 'center', color: '#6b7280' },
    emptyIcon: { fontSize: 40, marginBottom: 12 },
    emptyText: { fontSize: 15, marginBottom: 4 },
    emptyHint: { fontSize: 13 },
    
    // Manual mode
    manualGrid: { display: 'flex', flexDirection: 'column', gap: 8 },
    manualRow: { display: 'grid', gridTemplateColumns: '28px 1fr 1fr 1.2fr 1fr 32px', gap: 8, alignItems: 'center' },
    manualNum: { width: 24, height: 24, background: '#10B981', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600 },
    input: { padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14, width: '100%', boxSizing: 'border-box' },
    btnRemove: { width: 28, height: 28, background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
    btnAdd: { background: 'none', border: '2px dashed #d1d5db', padding: '12px', borderRadius: 8, color: '#6b7280', cursor: 'pointer', width: '100%', fontSize: 14, marginTop: 8 },
    
    // CSV mode
    csvBox: { background: 'white', padding: 16, borderRadius: 8, marginBottom: 16, border: '1px solid #e5e7eb' },
    pre: { background: '#f3f4f6', padding: 12, borderRadius: 6, marginTop: 10, fontSize: 13, overflow: 'auto', margin: '10px 0' },
    hint: { fontSize: 12, color: '#6b7280', marginTop: 8 },
    fileInput: { marginBottom: 16 },
    fileLoaded: { color: '#10B981', marginTop: 8, fontSize: 14 },
    
    saveCheck: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#6b7280', marginTop: 16 },
    
    // Summary
    summary: { background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: 16, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    summaryLeft: { display: 'flex', gap: 20 },
    summaryItem: { display: 'flex', flexDirection: 'column' },
    summaryLabel: { fontSize: 12, color: '#6b7280' },
    summaryValue: { fontSize: 18, fontWeight: 600, color: '#059669' },
    
    // Messages
    errorBox: { background: '#fef2f2', color: '#dc2626', padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
    successBox: { background: '#f0fdf4', color: '#059669', padding: 16, borderRadius: 8, marginBottom: 16 },
    
    // Actions
    actions: { display: 'flex', gap: 12, paddingTop: 20, borderTop: '1px solid #e5e7eb' },
    btnPrimary: { 
      flex: 1,
      background: 'linear-gradient(135deg, #10B981, #059669)', 
      color: 'white', 
      border: 'none', 
      padding: '14px 24px', 
      borderRadius: 10, 
      fontSize: 15, 
      fontWeight: 600, 
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s'
    },
    btnDisabled: { opacity: 0.5, cursor: 'not-allowed' }
  };

  // ============================================
  // СЕКЦИЯ: Рендер
  // ============================================
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>👥 Выбор получателей</h2>
        <p style={styles.subtitle}>
          {campaignType === 'b2b' 
            ? 'Выберите сотрудников для персональных AI-подарков' 
            : 'Выберите клиентов или лидов для кампании'}
        </p>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button 
          style={{...styles.tab, ...(mode === 'database' ? styles.tabActive : {})}} 
          onClick={() => { setMode('database'); setResult(null); setError(''); }}
        >
          👥 Из базы 
          <span style={{...styles.tabCount, ...(mode === 'database' ? styles.tabCountActive : {})}}>
            {contacts.length}
          </span>
        </button>
        <button 
          style={{...styles.tab, ...(mode === 'manual' ? styles.tabActive : {})}} 
          onClick={() => { setMode('manual'); setResult(null); setError(''); }}
        >
          ✏️ Вручную
        </button>
        <button 
          style={{...styles.tab, ...(mode === 'csv' ? styles.tabActive : {})}} 
          onClick={() => { setMode('csv'); setResult(null); setError(''); }}
        >
          📄 CSV файл
        </button>
      </div>

      {/* Database mode */}
      {mode === 'database' && (
        <div style={styles.section}>
          {loadingContacts ? (
            <p style={{ textAlign: 'center', padding: 20, color: '#6b7280' }}>Загрузка {contactTypeLabel}...</p>
          ) : contacts.length === 0 ? (
            <div style={styles.emptyDb}>
              <div style={styles.emptyIcon}>📭</div>
              <p style={styles.emptyText}>Нет {contactTypeLabel}</p>
              <p style={styles.emptyHint}>
                {campaignType === 'b2b' 
                  ? 'Добавьте сотрудников в разделе Контакты → Сотрудники' 
                  : 'Добавьте клиентов в разделе Контакты'}
              </p>
            </div>
          ) : (
            <>
              <div style={styles.dbHeader}>
                <span style={styles.dbInfo}>
                  Выбрано: <span style={styles.dbInfoCount}>{selectedContacts.length}</span> из {contacts.length}
                </span>
                <button style={styles.selectAllBtn} onClick={selectAll}>
                  {selectedContacts.length === contacts.length ? 'Снять всё' : 'Выбрать всех'}
                </button>
              </div>
              
              <div style={styles.dbList}>
                {contacts.map(contact => (
                  <div 
                    key={contact.id} 
                    style={{...styles.dbItem, ...(selectedContacts.find(c => c.id === contact.id) ? styles.dbItemSelected : {})}}
                    onClick={() => toggleContact(contact)}
                  >
                    <input 
                      type="checkbox" 
                      style={styles.checkbox}
                      checked={!!selectedContacts.find(c => c.id === contact.id)}
                      onChange={() => {}}
                    />
                    {contact.photo_url ? (
                      <img src={contact.photo_url} alt="" style={styles.dbPhoto} />
                    ) : (
                      <div style={styles.dbPhotoPlaceholder}>
                        {(contact.first_name || '?')[0]}{(contact.last_name || '')[0]}
                      </div>
                    )}
                    <div>
                      <div style={styles.dbName}>
                        {contact.first_name} {contact.last_name}
                        {!contact.photo_url && campaignType === 'b2b' && <span style={styles.noPhotoWarn}>⚠️</span>}
                        {campaignType && campaignType.startsWith('brand') && contact.contact_type && (
                          <span style={contact.contact_type === 'customer' ? styles.badgeCustomer : styles.badgeLead}>
                            {contact.contact_type === 'customer' ? '🛒' : '🎯'}
                          </span>
                        )}
                      </div>
                    </div>
                    <span style={styles.dbMeta}>{contact.email || '—'}</span>
                    <span style={styles.dbMeta}>{contact.phone || '—'}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Manual mode */}
      {mode === 'manual' && (
        <div style={styles.section}>
          <div style={{...styles.manualRow, marginBottom: 8, paddingLeft: 36}}>
            <span></span>
            <span style={{fontSize: 12, color: '#6b7280'}}>Имя *</span>
            <span style={{fontSize: 12, color: '#6b7280'}}>Фамилия</span>
            <span style={{fontSize: 12, color: '#6b7280'}}>Email</span>
            <span style={{fontSize: 12, color: '#6b7280'}}>Телефон</span>
            <span></span>
          </div>
          <div style={styles.manualGrid}>
            {manualContacts.map((contact, index) => (
              <div key={index} style={styles.manualRow}>
                <span style={styles.manualNum}>{index + 1}</span>
                <input style={styles.input} type="text" placeholder="Jean" value={contact.firstName} onChange={(e) => updateContact(index, 'firstName', e.target.value)} />
                <input style={styles.input} type="text" placeholder="Dupont" value={contact.lastName} onChange={(e) => updateContact(index, 'lastName', e.target.value)} />
                <input style={styles.input} type="email" placeholder="jean@company.fr" value={contact.email} onChange={(e) => updateContact(index, 'email', e.target.value)} />
                <input style={styles.input} type="tel" placeholder="+33612345678" value={contact.phone} onChange={(e) => updateContact(index, 'phone', e.target.value)} />
                <button style={{...styles.btnRemove, opacity: manualContacts.length === 1 ? 0.3 : 1}} onClick={() => removeContact(index)} disabled={manualContacts.length === 1}>✕</button>
              </div>
            ))}
          </div>
          <button style={styles.btnAdd} onClick={addContact}>+ Добавить получателя</button>
          <label style={styles.saveCheck}>
            <input type="checkbox" checked={saveToDb} onChange={(e) => setSaveToDb(e.target.checked)} style={{ width: 18, height: 18, accentColor: '#10B981' }} />
            Сохранить в базу контактов для повторного использования
          </label>
        </div>
      )}

      {/* CSV mode */}
      {mode === 'csv' && (
        <div style={styles.section}>
          <div style={styles.csvBox}>
            <strong>📋 Формат CSV:</strong>
            <pre style={styles.pre}>name,email,phone{'\n'}Jean Dupont,jean@company.fr,+33612345678{'\n'}Marie Laurent,marie@company.fr,+33698765432</pre>
            <p style={styles.hint}>* Обязательное поле: name. Опционально: email, phone</p>
          </div>
          <div style={styles.fileInput}>
            <input type="file" accept=".csv" onChange={handleFileSelect} />
            {csvContent && <p style={styles.fileLoaded}>✅ Файл загружен ({csvContent.split('\n').length - 1} записей)</p>}
          </div>
          <label style={styles.saveCheck}>
            <input type="checkbox" checked={saveToDb} onChange={(e) => setSaveToDb(e.target.checked)} style={{ width: 18, height: 18, accentColor: '#10B981' }} />
            Сохранить контакты в базу для повторного использования
          </label>
        </div>
      )}

      {/* Summary */}
      {mode === 'database' && selectedContacts.length > 0 && (
        <div style={styles.summary}>
          <div style={styles.summaryLeft}>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Получателей</span>
              <span style={styles.summaryValue}>{selectedContacts.length}</span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Сумма на человека</span>
              <span style={styles.summaryValue}>{rewardAmount} DAR</span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Итого</span>
              <span style={styles.summaryValue}>{totalAmount} DAR</span>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      {error && <div style={styles.errorBox}>❌ {error}</div>}
      {result && (
        <div style={styles.successBox}>
          <p style={{margin: 0}}><strong>✅ Получатели добавлены!</strong></p>
          <p style={{margin: '8px 0 0 0', fontSize: 14}}>Импортировано: {result.imported} {result.skipped > 0 && `• Пропущено: ${result.skipped}`}</p>
        </div>
      )}

      {/* Actions */}
      <div style={styles.actions}>
        <button 
          style={{...styles.btnPrimary, ...(loading ? styles.btnDisabled : {})}} 
          onClick={result ? onNext : handleUpload} 
          disabled={loading}
        >
          {loading ? '⏳ Загрузка...' : result ? 'Далее: проверка фото →' : '✓ Добавить получателей'}
        </button>
      </div>
    </div>
  );
};

export default CampaignUploadStep;